import { useAppState } from 'AppContextProvider'
import React, { Suspense, useCallback, useEffect, useMemo } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import mobileM1LoadingImage from 'Assets/Images/loading/m1-loading-mobile.webp'
import desktopM1LoadingImage from 'Assets/Images/loading/m1-loading-desktop.webp'
import { useDispatch } from 'store'
import { ActionType } from 'Reducers'
import { useExperiment } from '@growthbook/growthbook-react'
import { getSearchParams } from 'Lib'

const LoadingWithImageWrapper = React.lazy(async () => await import('./LoadingWithImageWrapper'))
const AnimatedLoadingScreen = React.lazy(async () => await import('Components/AnimatedLoadingScreen'))

const LoadingScreen = () => {
  const { t } = useTranslation()
  const forcedLoadingVariant = useMemo(() => {
    const params = getSearchParams()
    return params.get('loadingVariant')
  }, [])
  // this is a test for the ABTest calibration test
  const { value } = useExperiment({
    key: 'landing-calibration-test',
    variations: ['control', 'test'],
    hashAttribute: 'local_id' // This is important. Otherwise, GrowthBook will use the id attribute by default.
  })
  const { setAppState } = useAppState()
  const dispatch = useDispatch()
  const calibrationEventLogCountRef = React.useRef(0)

  const abtestCalibrationGroup = value ?? 'notset'

  const onComplete = useCallback(() => {
    // Remove loading screen class before navigating away
    document.body.classList.remove('loading-screen')

    dispatch({ type: ActionType.LOADING_COMPLETED })
  }, [dispatch])

  useEffect(() => {
    calibrationEventLogCountRef.current += 1
    const count = calibrationEventLogCountRef.current

    switch (abtestCalibrationGroup) {
      case 'notset':
        logEvent('web_abtest_calibration_not_set')
        logEvent('web_abtest_calibration_checkevent', { group: 'notset', count })
        break

      case 'control':
        Math.random() < 0.4 &&
        logEvent('web_abtest_calibration_checkevent', { group: 'control', count })
        break

      case 'test':
        Math.random() < 0.6 &&
        logEvent('web_abtest_calibration_checkevent', { group: 'test', count })
        break
    }
  }, [abtestCalibrationGroup])

  useEffect(() => {
    setAppState((prevState) => {
      const newState = { ...prevState }
      delete newState.currentStep
      delete newState.prevStep
      delete newState.steps

      return newState
    })

    dispatch({ type: ActionType.CLEAR_APP_DATA })
  }, [])

  useEffect(() => {
    // Allow cookie popup to be targeted through CSS only on all loading screens
    document.body.classList.add('loading-screen')

    return () => {
      document.body.classList.remove('loading-screen')
    }
  }, [])

  const LoadingComponent = useMemo(() => {
    if (forcedLoadingVariant === 'm1') {
      return (
        <LoadingWithImageWrapper
          desktopImageUrl={desktopM1LoadingImage}
          mobileImageUrl={mobileM1LoadingImage}
          title={
            <Trans
              i18nKey='loading.m1.title'
              t={t}
              components={{
                em: <em />,
                wbr: <wbr />
              }}
            />
          }
          subtitle={<Trans t={t} i18nKey='loading.m1.subtitle' />}
          onComplete={onComplete}
        />
      )
    }

    return <AnimatedLoadingScreen onComplete={onComplete} />
  }, [t, forcedLoadingVariant, onComplete])

  return (
    <Suspense fallback={null}>
      {LoadingComponent}
    </Suspense>
  )
}

export default LoadingScreen
